import moment from 'moment';

// Dates/times are displayed in US Eastern Time (EST/EDT) app-wide, regardless
// of the viewer's browser timezone.
export const ET_TIME_ZONE = 'America/New_York';

// Offset (in minutes) of America/New_York from UTC at the given instant —
// -240 during EDT, -300 during EST. Uses Intl so DST is handled automatically.
function getEtOffsetMinutes(date) {
  const localized = new Date(
    date.toLocaleString('en-US', { timeZone: ET_TIME_ZONE })
  );
  const utc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  return Math.round((localized - utc) / 60000);
}

// Values carrying a zone are instants; anything else is a wall-clock reading.
const ZONED = /Z$|[+-]\d{2}:?\d{2}$/;

/**
 * Render an instant in Eastern Time using a moment format string, independent
 * of the browser's timezone. Returns "" for null/invalid input.
 *
 * Zone-less strings (the API's "local date-time, no zone", e.g. an
 * assessmentDate of "2026-08-07T00:00:00") are wall-clock readings, not
 * instants. Converting them would shift the time by the viewer's offset and
 * could roll the date back a day, so they are rendered as written. Date objects
 * and zoned strings are real instants and do get converted.
 */
export function formatInEt(value, format) {
  const m = moment(value);
  if (!m.isValid()) return '';
  if (typeof value === 'string' && !ZONED.test(value)) return m.format(format);
  return m.utcOffset(getEtOffsetMinutes(m.toDate())).format(format);
}

// Parse an ISO string into a moment normalized so it can be rendered in ET.
// Timezone-aware strings are absolute instants; legacy strings without an
// offset are treated as UTC so they render consistently everywhere.
function parseToMoment(isoString) {
  const hasTimezone =
    isoString.includes('Z') ||
    isoString.includes('+') ||
    /-\d{2}:\d{2}$/.test(isoString);
  return hasTimezone
    ? moment.utc(isoString)
    : moment.utc(isoString.replace(' ', 'T'));
}

/**
 * Converts a UTC ISO 8601 timestamp to US Eastern Time and formats it as
 * "D MMM YYYY, h:mm A" (e.g. "4 Apr 2026, 7:00 PM").
 * Legacy strings without a timezone offset are treated as UTC.
 * Returns "—" for null/undefined/invalid values.
 */
export function formatUtcTimestamp(isoString) {
  if (!isoString) return '—';
  const m = parseToMoment(isoString);
  if (!m.isValid()) return '—';
  return m.utcOffset(getEtOffsetMinutes(m.toDate())).format('D MMM YYYY, h:mm A');
}

/**
 * Same as formatUtcTimestamp but returns only the date portion:
 * "D MMM YYYY" (e.g. "4 Apr 2026").
 */
export function formatUtcDate(isoString) {
  if (!isoString) return '—';
  const m = parseToMoment(isoString);
  if (!m.isValid()) return '—';
  return m.utcOffset(getEtOffsetMinutes(m.toDate())).format('D MMM YYYY');
}

/**
 * The wire format for an assessment date.
 *
 * An assessment date is the calendar day the clinician saw the patient, not an
 * instant. The API stores it as a LocalDateTime and returns it without a zone,
 * and formatInEt renders a zone-less string exactly as written. Sending it as a
 * UTC instant (Date.toISOString) let the server re-read it in its own zone,
 * which rolls the day backwards whenever the two zones differ — so it goes over
 * the wire as naive local wall-clock, which round-trips unchanged.
 *
 * Returns null for a missing or unparseable value; the API rejects a blank
 * assessment date, so callers should treat null as "nothing to save".
 */
export function toAssessmentDatePayload(value) {
  if (!value) return null;
  const m = parseAssessmentDate(value);
  return m ? m.format('YYYY-MM-DDTHH:mm:ss') : null;
}

/**
 * Read an assessment date back off the API. Zone-less strings are wall-clock
 * readings and are parsed as local, so the picker shows the day that was
 * chosen; anything carrying a zone is a real instant and converts normally.
 */
export function parseAssessmentDate(value) {
  if (!value) return null;
  const m =
    typeof value === 'string' && !ZONED.test(value)
      ? moment(value, moment.ISO_8601)
      : moment(value);
  return m.isValid() ? m : null;
}

/** The same, as a Date for the date pickers. */
export function toAssessmentDateValue(value) {
  const m = parseAssessmentDate(value);
  return m ? m.toDate() : null;
}

export function activeMenu(path) {
    return (window.location.pathname.startsWith(path)) ? 'text-white bg_active rounded-right' : 'text-black';
}

export function activeToggle(path1, path2) {
    return (path2 && window.location.pathname == path2) ? false : window.location.pathname !== '/hcp/patient/outstanding_list' && (window.location.pathname == path1 || window.location.pathname.startsWith(path1)) ? true : false;
}

export function activeStanding(path) {
    return window.location.pathname == path ? 'text-white bg_active rounded-right' : 'text-black';
}

export function activeText(path) {
    return window.location.pathname.startsWith(path) ? true: false;
}